import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { reportError } from "@/lib/errorReport";
import type { Profile, UserRole } from "@/types/database";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  role: UserRole | null;
  propertyId: string | null;
  companyId: string | null;
  loading: boolean;
  /** Set when the real profile could not be loaded and access is blocked. */
  fallback: boolean;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------
// Hard timeouts — nothing in this context is allowed to spin forever.
// ---------------------------------------------------------------------
// 30 s gives mobile connections on slow networks enough headroom without
// spinning forever. The previous 15 s was causing "getSession timed out"
// Sentry errors on legitimate logins over weak connections.
const PROFILE_TIMEOUT_MS = 30_000;
const SIGNIN_TIMEOUT_MS = 30_000;
const SESSION_TIMEOUT_MS = 30_000;

function withTimeout<T>(
  p: PromiseLike<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    Promise.resolve(p).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

// ---------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);
  // Guard against state updates after unmount
  const mountedRef = useRef(true);
  // user id whose profile is currently loaded — used to
  // dedupe concurrent fetches and to never downgrade a good profile on timeout.
  const profileUserIdRef = useRef<string | null>(null);
  // Monotonic sequence so a slow/stale fetch can't overwrite a newer result.
  const fetchSeqRef = useRef(0);
  // In-flight fetch promise per user. Bootstrap AND onAuthStateChange both
  // fire on app load (Supabase emits an INITIAL_SESSION event right after
  // the bootstrap's getSession resolves). Without this guard, two profile
  // queries race on supabase-js's internal LockManager and one gets
  // "lock was released because another request stole it" — which used to
  // degrade the user to a synthetic fallback profile. The dedupe on
  // profileUserIdRef alone isn't enough because that ref is only set
  // AFTER a successful fetch; concurrent first-time fetches never see it.
  const inFlightFetchRef = useRef<Map<string, Promise<void>>>(new Map());

  /**
   * Fetch the profile row. If the request fails OR doesn't resolve in
   * PROFILE_TIMEOUT_MS, expose fallback mode with no profile so route guards
   * block portal access until the trusted row is available.
   */
  const fetchProfile = useCallback(async (user: User): Promise<void> => {
    // ---------------------------------------------------------------
    // In-flight dedupe — if a fetch for this user is already running,
    // just await its result instead of firing a parallel request.
    // ---------------------------------------------------------------
    const existing = inFlightFetchRef.current.get(user.id);
    if (existing) {
      console.log("[Auth] 🔁 fetchProfile dedupe — joining in-flight for", user.email);
      return existing;
    }
    const promise = doFetchProfile(user);
    inFlightFetchRef.current.set(user.id, promise);
    try {
      return await promise;
    } finally {
      inFlightFetchRef.current.delete(user.id);
    }
  }, []);

  /**
   * The actual fetch implementation. Always go through fetchProfile() above
   * so the in-flight dedupe applies. Calling this directly would re-introduce
   * the race that the wrapper exists to prevent.
   */
  const doFetchProfile = useCallback(async (user: User): Promise<void> => {
    const seq = ++fetchSeqRef.current;
    // True if this result is no longer the latest (a newer fetch superseded it,
    // or the provider unmounted) — such results must NOT touch state.
    const stale = () => !mountedRef.current || seq !== fetchSeqRef.current;
    // True if we already hold a profile for this user — a failed/timed-out
    // fetch must never downgrade it to blocked fallback mode.
    const haveGood = () => profileUserIdRef.current === user.id;

    console.log("[Auth] 🔍 fetchProfile START for", user.email, "id=", user.id);
    const t0 = performance.now();
    try {
      const { data, error } = await withTimeout(
        supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
        PROFILE_TIMEOUT_MS,
        "profile fetch",
      );
      if (stale()) return;
      const dt = Math.round(performance.now() - t0);
      console.log(`[Auth] ✅ fetchProfile completed in ${dt}ms`, {
        hasData: !!data,
        error: error?.message,
      });

      if (error) {
        if (haveGood()) return; // keep the good profile we already have
        // Treat supabase-js LockManager races as transient — they look like
        // a `error.message` of "Lock 'lock:sb-...-auth-token' was released
        // because another request stole it". Synthesising a student-fallback
        // from a transient lock conflict is the bug that drops owners to the
        // /student layout. Surface as fallback-with-null-profile so the
        // route guards keep them out of the portal and the banner offers
        // Retry, instead of silent privilege downgrade.
        const msg = (error.message ?? "").toLowerCase();
        const isLockRace = msg.includes("lock was released") || msg.includes("stole it");
        if (isLockRace) {
          console.warn(
            "[Auth] ⚠️ transient supabase-js LockManager race during profile fetch — surfacing fallback banner (no synthetic profile).",
            error,
          );
          setProfile(null);
          setFallback(true);
          reportError(error, { scope: "auth.fetchProfile.lockRace", level: "warning" });
          return;
        }
        console.warn("[Auth] ⚠️ profile fetch error — blocking portal access until retry.", error);
        setProfile(null);
        setFallback(true);
        reportError(error, { scope: "auth.fetchProfile.error", level: "warning" });
        return;
      }

      if (data) {
        const p = data as Profile;
        setProfile(p);
        setFallback(false);
        profileUserIdRef.current = user.id;
        console.log("[Auth] 👤 profile loaded — role=", p.role);
      } else {
        if (haveGood()) return;
        console.warn("[Auth] ⚠️ no profile row found — blocking portal access until account is provisioned.");
        setProfile(null);
        setFallback(true);
      }
    } catch (err) {
      if (stale()) return;
      // A timeout/error must NOT clobber an already-loaded real profile — that
      // was the "owner dropped to student/limited-access" bypass bug.
      if (haveGood()) {
        console.warn("[Auth] ⏳ profile fetch timed out but a real profile is already loaded — keeping it.");
        return;
      }
      console.error("[Auth] ❌ fetchProfile FAILED:", (err as Error).message);
      // P0.4 fix (2026-05-28 audit): DO NOT synthesise a profile from the
      // email domain on first-load timeout. Inferring 'student' for an unknown
      // user is a silent privilege downgrade — an admin/owner whose initial
      // fetch happens to time out would land on the student portal with no
      // visible signal anything was wrong. Instead clear the profile + raise
      // the fallback flag so AuthFallbackBanner shows with a Retry button,
      // and route guards block portal access until the real profile loads.
      setProfile(null);
      setFallback(true);
      reportError(err, { scope: "auth.fetchProfile" });
    }
  }, []);

  // -----------------------------------------------------------------
  // Bootstrap on mount
  // -----------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;
    console.log("[Auth] 🚀 AuthProvider mount, bootstrap starting");
    const t0 = performance.now();

    (async () => {
      try {
        const { data } = await withTimeout(
          supabase.auth.getSession(),
          SESSION_TIMEOUT_MS,
          "getSession",
        );
        const dt = Math.round(performance.now() - t0);
        console.log(
          `[Auth] 🔐 getSession resolved in ${dt}ms, hasSession=`,
          !!data.session,
        );

        if (!mountedRef.current) return;
        setSession(data.session ?? null);

        if (data.session?.user) {
          await fetchProfile(data.session.user);
        }
      } catch (e) {
        console.error("[Auth] ❌ bootstrap failed:", (e as Error).message);
        reportError(e, { scope: "auth.bootstrap" });
      } finally {
        if (mountedRef.current) {
          console.log("[Auth] 🏁 bootstrap done — loading=false");
          setLoading(false);
        }
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log(
          "[Auth] 📡 onAuthStateChange event=",
          event,
          "hasSession=",
          !!newSession,
        );
        if (!mountedRef.current) return;
        setSession(newSession);

        if (newSession?.user) {
          // Dedupe: Supabase fires several events on load (INITIAL_SESSION,
          // SIGNED_IN, TOKEN_REFRESHED). If we already hold this user's real
          // profile, don't re-fetch — that storm of concurrent profile queries
          // is what starved some to the 15s timeout.
          if (profileUserIdRef.current === newSession.user.id) return;
          setLoading(true);
          await fetchProfile(newSession.user);
          if (mountedRef.current) {
            console.log("[Auth] 🏁 post-auth-change profile done — loading=false");
            setLoading(false);
          }
        } else {
          console.log("[Auth] 👋 session cleared — clearing profile");
          profileUserIdRef.current = null;
          setProfile(null);
          setFallback(false);
        }
      },
    );

    return () => {
      console.log("[Auth] 🧹 AuthProvider unmount");
      mountedRef.current = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // -----------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------
  const signIn = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
    console.log("[Auth] ✉️  signIn START for", email);
    const t0 = performance.now();
    try {
      const result = await withTimeout(
        supabase.auth.signInWithPassword({
          email,
          password,
          options: captchaToken ? { captchaToken } : undefined,
        }),
        SIGNIN_TIMEOUT_MS,
        "signIn",
      );
      const dt = Math.round(performance.now() - t0);
      if (result.error) {
        console.warn(
          `[Auth] ⚠️ signIn returned error in ${dt}ms:`,
          result.error.message,
        );
      } else {
        console.log(
          `[Auth] ✅ signIn OK in ${dt}ms — onAuthStateChange will follow`,
        );
      }
      return { error: result.error };
    } catch (e) {
      const dt = Math.round(performance.now() - t0);
      console.error(`[Auth] ❌ signIn FAILED after ${dt}ms:`, (e as Error).message);
      reportError(e, { scope: "auth.signIn", context: { email } });
      // Surface as an AuthError-shaped object the caller can render
      return {
        error: {
          name: "AuthError",
          message: (e as Error).message,
          status: 0,
        } as unknown as AuthError,
      };
    }
  },
  [],
  );

  const signOut = useCallback(async () => {
    console.log("[Auth] 🚪 signOut");
    try {
      await withTimeout(supabase.auth.signOut(), 5000, "signOut");
    } catch (e) {
      console.warn("[Auth] signOut timed out — clearing local state anyway");
      reportError(e, { scope: "auth.signOut", level: "warning" });
    }
    if (!mountedRef.current) return;
    profileUserIdRef.current = null;
    setProfile(null);
    setSession(null);
    setFallback(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user) {
      console.log("[Auth] 🔄 refreshProfile");
      await fetchProfile(session.user);
    }
  }, [session?.user, fetchProfile]);

  // -----------------------------------------------------------------
  // Context value
  // -----------------------------------------------------------------
  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role: profile?.role ?? null,
      propertyId: profile?.property_id ?? null,
      companyId: profile?.company_id ?? null,
      loading,
      fallback,
      signIn,
      signOut,
      refreshProfile,
    }),
    [session, profile, loading, fallback, signIn, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
